import { StrictMode, useMemo, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { CacheProvider } from '@emotion/react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Language } from '@mefosa/shared'
import './i18n'
import { applyDocumentLanguage, cachedLanguage } from './i18n'
import { buildTheme } from './theme'
import { cacheFor } from './theme/rtl'
import { AuthProvider } from './auth/AuthProvider'
import { ApiRequestError } from './api/client'
import App from './App'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry a permission or validation failure - it will not change.
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

/** Rebuilds the theme and Emotion cache whenever the language flips. */
function Localised({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const language = (i18n.language === 'ar' ? 'ar' : 'en') as Language
  const theme = useMemo(() => buildTheme(language), [language])
  const cache = useMemo(() => cacheFor(language === 'ar' ? 'rtl' : 'ltr'), [language])

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  )
}

applyDocumentLanguage(cachedLanguage())

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Localised>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Localised>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
